import React from 'react';
import { List, ListItem, spacer12, spacer20, Type, VisuallyHidden } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { DocumentLink } from 'lib/sanity/types';
import { useTranslation } from 'providers/translations/TranslationProvider';
import IconChevron from 'components/IconChevron';
import Ellipsis from 'components/Ellipsis';
import { ArticleLink } from 'components/Links';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';

interface SearchResultsProps {
  searchResults: DocumentLink[];
  ubi: UbiHomePage;
}

const Container = styled.div`
  padding-top: ${spacer12};
`;

const StyledArticleLink = styled(ArticleLink)`
  display: flex;
  flex: 1;
  justify-content: space-between;
  width: 100%;
`;

const StyledListItem = styled(ListItem)`
  height: 24px;
  padding: ${spacer20} 0;
  display: flex;
  align-items: center;
`;

const SearchResults = ({ searchResults, ubi }: SearchResultsProps) => {
  const { t } = useTranslation();

  return (
    <Container aria-label={t('search_results')}>
      {!searchResults.length && (
        <Type as="span" aria-live="polite" aria-atomic="true" data-testid="noResultsMessage" variant="ballad">
          <em>{t('search_results_no_results_description')}</em>
        </Type>
      )}

      {searchResults.length > 0 && (
        <>
          <VisuallyHidden data-testid="ariaMessage" role="status" aria-live="polite" aria-atomic="true">
            {searchResults.length} {t('search_results_found')}
          </VisuallyHidden>
          <List data-testid="resultList">
            {searchResults.map((article: DocumentLink, index: number) => (
              <StyledListItem key={article.slug} role="listitem">
                <StyledArticleLink
                  data-testid={`${article.title}-Link`}
                  slug={article.slug}
                  eventDefinition={{
                    category: 'search-dropdown',
                    action: `click-index-${index + 1}`,
                    label: article.slug,
                  }}
                  ubiFactory={ubi.logSearchResult(article.slug, index)}
                >
                  <Ellipsis variant="ballad">{article.title}</Ellipsis>
                  <IconChevron />
                </StyledArticleLink>
              </StyledListItem>
            ))}
          </List>
        </>
      )}
    </Container>
  );
};

export default SearchResults;
